import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { USER_ACCESS_LEVEL } from '../../../utils/constants';

/**
 * Solutions > Overview — Common Section screen, identical in the Partner,
 * School, School Network and Teacher workspaces (see docs/features nav
 * sheets: PW_SO_OV / SW_SO_OV / SNW_SO_OV / TW_SO_OV, plus the bare
 * /dq_pro/solutions landing which shows the same content). Deliberately kept
 * OUT of any persona folder (TeacherDashboard, DQProV2, etc.) since it
 * belongs to all four.
 *
 * Layout/copy only came from the reference mockup — colors, width and
 * component shapes follow this app's own brand tokens and the conventions
 * already established elsewhere (DQProHome.page.jsx, ManageClasses.page.jsx),
 * not the mockup's own (sample) palette.
 *
 * The COS Government Checklist is Partner-workspace only (it's the one
 * solution absent from schoolWorkspace.nav.js / networkWorkspace.nav.js /
 * teacherWorkspace.nav.js), so its card is hidden for anyone who isn't a
 * true Partner (access level 20) rather than showing a card whose
 * destinations aren't in their own sidebar. `isOwner()` from
 * productAccess.js is too broad here — it also returns true for level 21
 * (FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE), which is the **School
 * Network Workspace's** own access level (School Workspace is a distinct,
 * separate level — 22, PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE),
 * so that check let School Network owners see this card too.
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

const INCLUDED_IN_READINESS = [
    { label: 'Measure', value: 'COS Children Assessment' },
    { label: 'Build', value: 'LearnCreator' },
    { label: 'Track', value: 'Progress dashboards' },
];

const DISTRIBUTABLE_SOLUTIONS = [
    {
        key: 'children-assessment',
        title: 'COS Children Assessment',
        desc: "Shows how ready students are for life online — where they're doing well, where they're at risk, and where they need support.",
        overviewPath: '/dq_pro/solutions/children-assessment/overview',
        previewPath: '/dq_pro/solutions/children-assessment/preview',
    },
    {
        key: 'learncreator',
        title: 'LearnCreator',
        desc: 'Generates complete, age-appropriate learning programs with assessment built in — in days rather than months.',
        overviewPath: '/dq_pro/solutions/learncreator/overview',
        previewPath: '/dq_pro/solutions/learncreator/preview',
    },
    {
        key: 'government-checklist',
        title: 'COS Government Checklist',
        badge: 'Government only',
        desc: "Evaluates a country's child online protection system across 85 indicators — laws, governance, education, families, industry, and infrastructure. Distributed to national governments and their designated entities, not to schools.",
        overviewPath: '/dq_pro/solutions/government-checklist/overview',
        // No previewPath: there is no Government Checklist preview yet (Ian, 23 Sep).
        partnerOnly: true,
    },
];

// Same solid-orange / outlined-grey pill pair used throughout the app's own
// dialogs (AddClassStudentsDialog, LinkProgramTypeChooserDialog, etc.).
const primaryButtonSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '50px',
    px: 2.75,
    py: 1,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00' },
};

const outlineButtonSx = {
    borderColor: ORANGE,
    color: ORANGE,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '50px',
    px: 2.75,
    py: 1,
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255, 105, 0, 0.06)' },
};

// Neutral pill — same style as the "Optional" tag on the Add-a-Class dialog —
// reused here instead of inventing a new semantic (e.g. amber) color.
const neutralBadgeSx = {
    fontSize: '11.5px',
    fontWeight: 600,
    color: MUTED,
    border: `1px solid ${BORDER}`,
    borderRadius: '999px',
    px: 1.5,
    py: 0.4,
};

const SolutionsOverviewPage = () => {
    const history = useHistory();
    const { authUser } = useAuth();
    // Partner only (level 20) — NOT isOwner(), which also passes level 21,
    // the School Network Workspace's own access level (School Workspace is
    // a separate level, 22).
    const canSeeGovernmentChecklist =
        authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL
        && authUser?.is_peer !== true;
    const visibleSolutions = DISTRIBUTABLE_SOLUTIONS.filter((s) => !s.partnerOnly || canSeeGovernmentChecklist);

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
            <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1 }}>
                Child Online Safety (COS) Solutions
            </Typography>
            <Typography sx={{ fontSize: '15px', color: MUTED, mb: 3.5, lineHeight: 1.6 }}>
                Four solutions, built to work together. Start with the complete system, or distribute any part on its own.
            </Typography>

            {/* Complete system */}
            <Box sx={{
                border: `1px solid ${BORDER}`, borderRadius: '12px',
                boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
                p: { xs: 2.5, sm: 4 }, mb: 4,
                backgroundColor: 'var(--brand-surface, #ffffff)',
            }}>
                <Box sx={{
                    display: 'inline-block', fontSize: '12px', fontWeight: 700, color: ORANGE,
                    backgroundColor: 'rgba(255, 105, 0, 0.08)', borderRadius: '999px',
                    px: 1.5, py: 0.5, mb: 2,
                }}>
                    Complete system
                </Box>
                <Typography sx={{ fontSize: { xs: '20px', sm: '26px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
                    COS Readiness Solution
                </Typography>
                <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 2.5,}}>
                    Measure where every student stands, build the lessons that close the gap, and track growth over
                    time — as its own course or inside subjects schools already teach.
                </Typography>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: MUTED, mb: 1.5 }}>
                    Includes all three
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 3 }}>
                    {INCLUDED_IN_READINESS.map((item) => (
                        <Box key={item.label} sx={{ backgroundColor: 'var(--brand-bg, #FFFBF7)', borderRadius: '10px', p: 2 }}>
                            <Typography sx={{ fontSize: '12px', color: MUTED, mb: 0.5 }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>
                                {item.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Button sx={primaryButtonSx} onClick={() => history.push('/dq_pro/solutions/readiness/overview')}>
                    See how it works
                </Button>
            </Box>

            <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: MUTED, mb: 2 }}>
                Or distribute separately
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {visibleSolutions.map((s) => (
                    <Box key={s.key} sx={{
                        border: `1px solid ${BORDER}`, borderRadius: '12px',
                        boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
                        p: { xs: 2.5, sm: 3.5 },
                        backgroundColor: 'var(--brand-surface, #ffffff)',
                        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2,
                    }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Typography sx={{ fontSize: '15px', fontWeight: 800, color: TEXT }}>
                                    {s.title}
                                </Typography>
                                {s.badge && (
                                    <Box sx={neutralBadgeSx}>{s.badge}</Box>
                                )}
                            </Box>
                            <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.6, }}>
                                {s.desc}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.25, flexShrink: 0 }}>
                            <Button sx={primaryButtonSx} onClick={() => history.push(s.overviewPath)}>
                                Overview
                            </Button>
                            {s.previewPath && (
                                <Button variant="outlined" sx={outlineButtonSx} onClick={() => history.push(s.previewPath)}>
                                    Preview
                                </Button>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default SolutionsOverviewPage;
